import { useEffect, useState } from 'react'
import { api } from '../../api/client'

/**
 * Der App-Zugang eines Fahrers: QR-Code erzeugen, ausdrucken, verschicken.
 *
 * Der Code ist eine einmalige Kopplung. Er wird beim Erzeugen genau einmal angezeigt
 * beziehungsweise ausgegeben und ist danach nicht wieder abrufbar — deshalb erzeugt jeder
 * Knopf hier einen neuen und zieht den vorherigen zurück. Das steht auch so auf der Seite:
 * Wer zweimal auf „PDF" drückt, soll wissen, dass der erste Ausdruck damit ungültig ist.
 */

interface CodeAntwort {
  id: number
  fahrerId: number
  fahrer: string
  personalnummer: string
  /** null = laeuft nicht ab (Einstellung kopplung.gueltigkeit-tage = 0). */
  gueltigBis: string | null
  eingeloestAm: string | null
  widerrufenAm: string | null
  offen: boolean
  /** Wie oft dieser Code schon gekoppelt hat. */
  einloesungen: number
  /** Obergrenze des Betriebs — 1 im Normalfall, höher nur für die Prüfung in den Stores. */
  maxEinloesungen: number
}

interface Eigenschaften {
  fahrerId: number
  name: string
  personalnummer: string
  hatMailadresse: boolean
  beiSchliessen: () => void
}

function alsZeitpunkt(iso: string | null): string {
  if (!iso) return '–'
  const d = new Date(iso)
  return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function AppZugang({
  fahrerId,
  name,
  personalnummer,
  hatMailadresse,
  beiSchliessen,
}: Eigenschaften) {
  const [codes, setCodes] = useState<CodeAntwort[]>([])
  const [qrBild, setQrBild] = useState<string | null>(null)
  // Derselbe Code zum Abtippen. Ohne ihn ist der Zugang für jeden unbrauchbar, der nicht
  // scannen kann — allen voran der Simulator der App-Stores, der keine Kamera hat.
  const [klartext, setKlartext] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function laden() {
    try {
      setCodes(await api.get<CodeAntwort[]>(`/api/kopplung/fahrer/${fahrerId}`))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    }
  }

  useEffect(() => {
    void laden()
    // Den angezeigten Code beim Wechsel des Fahrers wegräumen: Er darf nicht neben dem
    // Namen eines anderen stehenbleiben.
    return () => {
      setQrBild((alt) => {
        if (alt) URL.revokeObjectURL(alt)
        return null
      })
      setKlartext(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fahrerId])

  async function qrAnzeigen() {
    setLaeuft(true)
    setMeldung(null)
    try {
      const { blob, kopf } = await api.holeBlobMitKopf(
        `/api/kopplung/fahrer/${fahrerId}/qr`,
        'POST',
      )
      if (qrBild) URL.revokeObjectURL(qrBild)
      setQrBild(URL.createObjectURL(blob))
      setKlartext(kopf.get('X-Kopplungscode'))
      setMeldung({
        art: 'erfolg',
        text: 'Neuer Code erzeugt. Der Fahrer kann ihn jetzt abscannen — ein früherer Code gilt nicht mehr.',
      })
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  async function pdfHolen() {
    setLaeuft(true)
    setMeldung(null)
    try {
      await api.herunterladen(
        `/api/kopplung/fahrer/${fahrerId}/pdf`,
        `Zugang ${personalnummer}.pdf`,
        'POST',
      )
      setMeldung({ art: 'erfolg', text: 'PDF erzeugt. Ein früherer Code gilt nicht mehr.' })
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  async function perMail() {
    setLaeuft(true)
    setMeldung(null)
    try {
      const e = await api.post<{ verschickt: number; ohneMailadresse: number; protokoll: string[] }>(
        '/api/kopplung/sammel/mail',
        { fahrerIds: [fahrerId] },
      )
      setMeldung({
        art: e.verschickt > 0 ? 'erfolg' : 'fehler',
        text: e.protokoll.join(' · '),
      })
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Versand fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  async function widerrufen(id: number) {
    if (!confirm('Diesen Code zurückziehen? Ein bereits ausgeteilter Ausdruck wird damit ungültig.'))
      return
    try {
      await api.post(`/api/kopplung/${id}/widerrufen`)
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Fehlgeschlagen' })
    }
  }

  const offener = codes.find((c) => c.offen)
  // Die Obergrenze steht an jedem Code; ohne ausgegebenen Code gilt der Normalfall.
  const grenze = codes[0]?.maxEinloesungen ?? 1

  return (
    <div className="karte abschnitt">
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}
      >
        <div>
          <h2 style={{ margin: 0 }}>App-Zugang für {name}</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--grau-500)' }}>
            Personalnummer {personalnummer}
          </p>
        </div>
        <button className="knopf-schlicht" onClick={beiSchliessen}>
          Schließen
        </button>
      </div>

      <div className="meldung hinweis" style={{ marginTop: 14 }}>
        {grenze > 1 ? (
          <>
            Dieser Betrieb erlaubt <strong>{grenze} Kopplungen je Code</strong> — eine
            Einstellung für die Prüfung in den App-Stores. Im Normalfall ist ein Code nach
            einem Gerät verbraucht.
          </>
        ) : (
          <>Der QR-Code koppelt <strong>ein</strong> Gerät und ist danach verbraucht.</>
        )}{' '}
        Jeder der folgenden Knöpfe erzeugt einen <strong>neuen</strong> Code und zieht einen
        früheren zurück — ein bereits ausgeteilter Ausdruck wird damit ungültig.
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      <div className="werkzeugleiste" style={{ marginTop: 14, gap: 10 }}>
        <button className="knopf-primaer" disabled={laeuft} onClick={() => void qrAnzeigen()}>
          Code anzeigen
        </button>
        <button className="knopf-schlicht" disabled={laeuft} onClick={() => void pdfHolen()}>
          Als PDF herunterladen
        </button>
        <button
          className="knopf-schlicht"
          disabled={laeuft || !hatMailadresse}
          title={hatMailadresse ? undefined : 'Für diesen Fahrer ist keine Mailadresse hinterlegt'}
          onClick={() => void perMail()}
        >
          Per E-Mail schicken
        </button>
      </div>

      {qrBild && (
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <img
            src={qrBild}
            alt={`Kopplungscode für ${name}`}
            style={{ width: 300, height: 300, border: '1px solid var(--grau-200)' }}
          />
          {klartext && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  fontFamily: 'var(--schrift-mono)',
                  fontSize: '1.05rem',
                  letterSpacing: '0.04em',
                  userSelect: 'all',
                }}
              >
                {klartext.replace(/(.{4})/g, '$1 ').trim()}
              </div>
              <p style={{ color: 'var(--grau-500)', fontSize: '0.8rem', margin: '4px 0 0' }}>
                Zum Eintippen, wenn keine Kamera zur Verfügung steht — Groß- und
                Kleinschreibung beachten, Leerzeichen weglassen.
              </p>
            </div>
          )}
          <p style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
            Der Fahrer öffnet die App, wählt „Mit QR-Code anmelden" und hält das Gerät davor.
            {offener?.gueltigBis && ` Einzulösen bis ${alsZeitpunkt(offener.gueltigBis)}.`}
          </p>
        </div>
      )}

      <h3 style={{ marginTop: 22 }}>Ausgegebene Codes</h3>
      <div className="tabelle-huelle">
        <table>
          <thead>
            <tr>
              <th>Gültig bis</th>
              <th>Stand</th>
              <th>Eingelöst</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 ? (
              <tr>
                <td colSpan={4} className="leer">
                  Für diesen Fahrer wurde noch kein Zugang erzeugt.
                </td>
              </tr>
            ) : (
              codes.map((c) => (
                <tr key={c.id}>
                  <td>{c.gueltigBis ? alsZeitpunkt(c.gueltigBis) : <span style={{ color: 'var(--grau-500)' }}>läuft nicht ab</span>}</td>
                  <td>
                    {/* Reihenfolge: „offen" zuerst. Erlaubt der Betrieb mehrere
                        Kopplungen, ist ein eingelöster Code weiterhin offen — die
                        Prüfung auf eingeloestAm zuerst hätte ihn fälschlich als
                        verbraucht ausgewiesen. */}
                    {c.widerrufenAm ? (
                      <span className="etikett grau">zurückgezogen</span>
                    ) : c.offen ? (
                      <span className="etikett orange">
                        {c.maxEinloesungen > 1
                          ? `offen · ${c.einloesungen} von ${c.maxEinloesungen}`
                          : 'offen'}
                      </span>
                    ) : c.einloesungen > 0 ? (
                      <span className="etikett gruen">
                        {c.maxEinloesungen > 1 ? 'aufgebraucht' : 'gekoppelt'}
                      </span>
                    ) : (
                      <span className="etikett grau">abgelaufen</span>
                    )}
                  </td>
                  <td>{alsZeitpunkt(c.eingeloestAm)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {c.offen && (
                      <button
                        className="knopf-schlicht"
                        style={{ padding: '4px 10px', color: 'var(--rot-500)' }}
                        onClick={() => void widerrufen(c.id)}
                      >
                        Zurückziehen
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
